/**
 * Cluster API client.
 *
 * Public, read-only endpoints (currently `/api/cluster/plans`) plus future
 * authenticated endpoints can live here. We re-use the same axios instance
 * shape and env-resolution strategy as `auth-api.js` so the unified-response
 * unwrapping is consistent across services.
 *
 * Phase G v1.1 Sprint 2 introduced the public plans listing — used by the
 * Pricing page and the BuyPlan wizard. Both views are accessible to
 * unauthenticated visitors, so the request interceptor only attaches a
 * Bearer token when one is available (it does not require one).
 */
import axios from 'axios'
import { attachOrgLockedHandler } from '@/services/org-locked'
import store from '@/store'
import { envConfig } from '@/utils/env-config'
import { readToken, ACCESS_TOKEN_KEY } from '@/utils/token-storage'
import { createCertIssueApi, followProgress } from '@/utils/certs-issue-core'
import { createAppViewApi, createHostnameLaneApi } from '@/utils/certs-app-view'
import { loadDeliveryTarget } from '@/utils/domains-core'
import { loadCertificatesUnderDomain } from '@/utils/certs-under-domain'

const BASE_URL = envConfig.VUE_APP_API_BASE_URL || envConfig.VITE_API_BASE_URL
let API_VERSION = envConfig.VUE_APP_API_VERSION || envConfig.VITE_API_VERSION || ''
API_VERSION = API_VERSION.replace(/['"]/g, '').trim()
if (API_VERSION === 'v1' || API_VERSION === '/v1' || API_VERSION === 'api/v1') {
  API_VERSION = ''
}

const clusterAxios = axios.create({
  baseURL: API_VERSION ? `${BASE_URL}/api/${API_VERSION}` : `${BASE_URL}/api`,
  timeout: envConfig.VUE_APP_API_TIMEOUT || envConfig.VITE_API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Best-effort token attach. Plans endpoint is public, but we still send the
// token if we have one — useful for any per-user pricing logic the backend
// may add later (e.g. partner discounts).
clusterAxios.interceptors.request.use((config) => {
  const token = store.state.auth?.accessToken || readToken(ACCESS_TOKEN_KEY)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Unwrap the unified response shape: { success, message, data, ... }.
// Throws a descriptive Error when success === false so callers can use a
// simple try/catch instead of inspecting the envelope.
function unwrap(response) {
  const body = response?.data
  if (body && typeof body === 'object' && 'success' in body) {
    if (body.success === false) {
      const err = new Error(body.message || 'Request failed')
      err.response = response
      throw err
    }
    return body.data
  }
  return body
}

const certIssueApi = createCertIssueApi(clusterAxios, { unwrap })
const certAppViewApi = createAppViewApi(clusterAxios, { unwrap })
const certLaneApi = createHostnameLaneApi(clusterAxios, { unwrap })

export const clusterApi = {
  /**
   * List active subscription plans.
   * Returns an array of plan objects; each plan includes a `prices` array
   * with one entry per supported currency.
   *
   * W2 (trial+plan contract): cluster-api's public GET /api/cluster/plans
   * already returns only `is_active && is_listed` plans (design §7A). The
   * website is a pure render surface — it renders whatever the API returns
   * and MUST NOT add client-side assumptions about which plans exist.
   * Custom/private plans are intentionally absent from this response.
   */
  async getPlans() {
    const response = await clusterAxios.get('/cluster/plans')
    return unwrap(response) || []
  },

  /**
   * Fetch a single plan by id.
   * Used by the BuyPlan wizard to confirm the plan exists and to render
   * the plan-summary side panel.
   */
  async getPlan(planId) {
    const response = await clusterAxios.get(`/cluster/plans/${planId}`)
    return unwrap(response)
  },

  /**
   * Find the trial-enabled plan from the public plans listing.
   *
   * The trial+plan contract (W1/W3) says trial periods live on the
   * trial-enabled plan's `trial` block returned by GET /api/cluster/plans.
   * There's exactly one trial-enabled plan in practice (e.g. Starter), so we
   * return the first plan whose `trial.enabled === true`.
   *
   * INTEGRATION NOTE: the `trial` block + `plan_version` are not live on
   * GET /api/cluster/plans yet (parallel cluster-api branch). Until they
   * ship, no plan carries a `trial.enabled` flag and this returns null —
   * every caller degrades to its static fallback copy.
   *
   * @param {Array} [plans] optional pre-fetched plans list to avoid a
   *        second network round-trip; fetched via getPlans() when omitted.
   * @returns {Promise<Object|null>} the trial-enabled plan, or null.
   */
  async findTrialPlan(plans) {
    let list = plans
    if (!Array.isArray(list)) {
      try {
        list = await this.getPlans()
      } catch (_) {
        return null
      }
    }
    if (!Array.isArray(list)) return null
    return list.find((p) => p && p.trial && p.trial.enabled === true) || null
  },

  /**
   * Fetch the live trial terms (periods + agreement HTML + plan_version).
   *
   * Two resolution paths, in preference order (per the contract's open
   * coordination item #1 — the website prefers the thin endpoint):
   *   1. Thin endpoint: GET /api/cluster/plans/trial-terms — cheap, purpose
   *      built. Preferred when present.
   *   2. Fallback: read the `trial` block off the trial-enabled plan in the
   *      full plans list (findTrialPlan).
   *
   * The returned shape mirrors the plan `trial` block:
   *   { enabled, plan_version, trial_period_human, grace_period_human,
   *     predelete_warn_human, agreement_terms_html }
   * with `plan_version` lifted from the parent plan when we fall back to the
   * plans list.
   *
   * INTEGRATION NOTE: neither path is live yet (parallel cluster-api
   * branch). Both degrade to null so the Pricing FAQ + Terms-of-Service page
   * keep their existing static copy until the backend ships.
   *
   * @param {Array} [plans] optional pre-fetched plans list for the fallback
   *        path, so a caller that already loaded plans isn't charged a
   *        second request.
   * @returns {Promise<Object|null>} the trial-terms block, or null.
   */
  async getTrialTerms(plans) {
    // Path 1 — thin, preferred endpoint.
    try {
      const response = await clusterAxios.get('/cluster/plans/trial-terms')
      const data = unwrap(response)
      if (data && typeof data === 'object') {
        // Accept either a bare trial block or a wrapper { trial, plan_version }.
        const trial = data.trial && typeof data.trial === 'object' ? data.trial : data
        if (trial && (trial.trial_period_human || trial.agreement_terms_html || trial.enabled)) {
          return {
            ...trial,
            plan_version: trial.plan_version ?? data.plan_version ?? null
          }
        }
      }
    } catch (_) {
      // 404 / 501 / network — fall through to the plans-list path.
    }

    // Path 2 — fallback: read the trial block off the trial-enabled plan.
    const plan = await this.findTrialPlan(plans)
    if (plan && plan.trial && typeof plan.trial === 'object') {
      return {
        ...plan.trial,
        plan_version: plan.trial.plan_version ?? plan.plan_version ?? null
      }
    }
    return null
  },

  /**
   * Read the vCluster isolation / trial status for a user+org (FR6).
   *
   * Maps to GET /api/cluster/vclusters/isolation-status?user_id=&organization_id=
   * which returns { provisioned, tier, status, suspended, ... }. Used by
   * TrialStatusBanner (W5) to render trial countdown / paused / grace
   * states, and by W6 to poll the dedicated "provisioning…" state.
   *
   * Degrades gracefully: any error / 404 (endpoint not live, or no vCluster
   * yet for this user) resolves to null so the banner simply hides.
   *
   * @param {Object} params
   * @param {number|string} params.userId
   * @param {number|string} params.organizationId
   * @returns {Promise<Object|null>}
   */
  async getIsolationStatus({ userId, organizationId } = {}) {
    if (!userId && !organizationId) return null
    try {
      const params = {}
      if (userId !== undefined && userId !== null) params.user_id = userId
      if (organizationId !== undefined && organizationId !== null) {
        params.organization_id = organizationId
      }
      const response = await clusterAxios.get('/cluster/vclusters/isolation-status', { params })
      return unwrap(response) || null
    } catch (_) {
      return null
    }
  },

  // ── Certificate management (env-console Certificates tab, Cert Manager
  //    Cycle 3 — the `add-tls-certificate` customer journey) ──────────────────
  // All routes are org/domain-scoped on the backend (@require_permission
  // 'certificate', scope_param='domain') and reach the local cluster-api on
  // :5006 through the same `/api/cluster` vite proxy split as getPlans().

  /** BYO PEM: list stored certificates (each flags whether its key is SOPS
   *  encrypted). Returns `{ certificates, count }`. */
  async listManagedCertificates() {
    const response = await clusterAxios.get('/cluster/certificates/managed')
    return unwrap(response) || { certificates: [], count: 0 }
  },

  /** BYO PEM: store a certificate. Body: { domain, certificate, private_key,
   *  ca_certificate? }. Private key is encrypted at rest via SOPS server-side;
   *  a 503 means the secret backend is not configured. */
  async createManagedCertificate(payload) {
    const response = await clusterAxios.post('/cluster/certificates/managed', payload)
    return unwrap(response)
  },

  /** BYO PEM: metadata + public cert for one domain (never the private key). */
  async getManagedCertificate(domain) {
    const response = await clusterAxios.get(
      `/cluster/certificates/managed/${encodeURIComponent(domain)}`
    )
    return unwrap(response)
  },

  /** BYO PEM: delete a stored certificate (cert + CA + encrypted key). */
  async deleteManagedCertificate(domain) {
    const response = await clusterAxios.delete(
      `/cluster/certificates/managed/${encodeURIComponent(domain)}`
    )
    return unwrap(response)
  },

  /**
   * `GET /cluster/certificates/app-view?cluster=&namespace=&workload=&template_id=`
   * — the app's OWN hostnames and their certificate state, plus the issuable
   * group the request dialogue pre-fills from. Never throws: a 404 (endpoint
   * not deployed on this environment) answers `{ ok: false, missing: true }`
   * and the tab falls back to the certificate list.
   */
  async getCertificateAppView(params) {
    return certAppViewApi.load(params)
  },

  /**
   * `POST /cluster/certificates/hostname-lanes` `{hostnames: []}` — which lane
   * the platform's own evidence recommends for names that have no app view
   * behind them (the SSL tab on `/my/domains`, and a name typed by hand). Read
   * only: DNS lookups and nothing else. Never throws — a 404 or any failure
   * answers `{ ok: false, lanes: null }`, and no reading means every name stays
   * private, which is the behaviour this seeding replaces.
   */
  async classifyHostnameLanes(names) {
    return certLaneApi.classify(names)
  },

  /** ACME: list cert-manager Certificates, optionally filtered to this
   *  environment's cluster + namespace. Returns `{ certificates, count }`. */
  async listAcmeCertificates({ cluster, namespace } = {}) {
    const params = {}
    if (cluster) params.cluster = cluster
    if (namespace) params.namespace = namespace
    const response = await clusterAxios.get('/cluster/certificates/acme', { params })
    return unwrap(response) || { certificates: [], count: 0 }
  },

  // NOTE: the legacy `POST /cluster/certificates/acme/request` binding is gone.
  // It created a raw cert-manager Certificate with an HTTP-01 challenge and no
  // DNS work, so a private hostname could never validate and the caller was
  // left polling for ever with no reason. Every request now goes through
  // `ensureCertificateRegistration` + `followCertificateProgress` below.

  // ── Dev Environments (P8) — node picker for the create flow ─────────────────
  /**
   * List a cluster's nodes for the dev-environment node picker. Maps to
   * `GET /api/cluster/management/nodes?cluster_name=<c>&schedulable_only=false`
   * (we WANT cordoned nodes back so the picker can list them disabled, mirroring
   * the operator card). Returns `data[]` of
   * `{ name, role, ready, schedulable, unschedulable, taints, blocking_taints,
   *    labels_of_interest, is_dev_node, label, description }`; rank them with
   * `pickableNodes()` in utils/dev-env-shape.js.
   */
  async listClusterNodes(clusterName) {
    const response = await clusterAxios.get('/cluster/management/nodes', {
      params: { cluster_name: clusterName, schedulable_only: 'false' }
    })
    return unwrap(response) || []
  },

  /** ACME: fetch one Certificate's live status (poll target after request). */
  async getAcmeCertificate({ cluster, namespace, name }) {
    const response = await clusterAxios.get(
      `/cluster/certificates/acme/${encodeURIComponent(cluster)}/` +
      `${encodeURIComponent(namespace)}/${encodeURIComponent(name)}`
    )
    return unwrap(response)
  },

  // ── Certificates — customer Issue view (Unified Certificate Service 3.5f) ──
  // The §6.7 one-click flow behind the grantable `certificate.issue` (A-17):
  // PUT …/registrations/ensure (source manual) → GET …/progress (SSE with
  // polling fallback) → POST …/generate on Retry; POST …/upload is the BYO
  // source (A-18). Contract + reducers live in utils/certs-issue-core.js so
  // `node --test` covers them; this is the axios binding only.

  /** `PUT /cluster/certificates/registrations/ensure` → `{ ok, status, action, state, registrationId, … }` (never throws on a business refusal). */
  async ensureCertificateRegistration(body) {
    return certIssueApi.ensure(body)
  },

  /** `GET /cluster/certificates/registrations/<id>/progress` → the eight-step document. */
  async getCertificateProgress(registrationId, opts) {
    return certIssueApi.progress(registrationId, opts)
  },

  /** `POST /cluster/certificates/registrations/<id>/generate` (Retry — resumes at the failed step). */
  async generateCertificate(registrationId, opts) {
    return certIssueApi.generate(registrationId, opts)
  },

  /** `POST /cluster/certificates/registrations/<id>/upload` (BYO PEM; refusals surface verbatim). */
  async uploadCertificateToRegistration(registrationId, body) {
    return certIssueApi.upload(registrationId, body)
  },

  /**
   * `POST /cluster/certificates/registrations/<id>/bind-ingresses` — re-point
   * this environment's Ingresses at the delivered Secret. Delivery does this
   * by itself; the manual call is for registrations delivered before the bind
   * shipped, and to re-assert it after a hand-edited Ingress. Needs
   * `certificate.issue`; returns the raw outcome for `normalizeBindResult`.
   */
  async bindCertificateIngresses(registrationId) {
    return certIssueApi.bindIngresses(registrationId)
  },

  /** `POST /cluster/certificates/registrations/<id>/validate` (step 3 on demand; never orders). */
  async validateCertificateRegistration(registrationId) {
    return certIssueApi.validate(registrationId)
  },

  /** `GET /cluster/certificates/registrations?org_id=&cluster=&state=` → `{ registrations, count }`. */
  async listCertificateRegistrations(params) {
    return certIssueApi.list(params)
  },

  /**
   * `GET /cluster/domains/<domain>/delivery-target` (A-27) → `{ source, target, reason }`:
   * `source: 'domain'` with a `{cluster, namespace, secretName}` target (or null + `reason`),
   * `source: 'apps'` when the route is missing here (fall back to the org's deployed apps).
   */
  async getDomainDeliveryTarget(domain) {
    return loadDeliveryTarget(clusterAxios, domain, { unwrap })
  },

  /** `PATCH /cluster/certificates/registrations/<id>` `{hostnames:[{name, private}]}` — flips Private toggles (needs certificate.issue). */
  async updateCertificateRegistration(registrationId, body) {
    const response = await clusterAxios.patch(
      `/cluster/certificates/registrations/${encodeURIComponent(registrationId)}`, body
    )
    return unwrap(response)
  },

  // ── Certificates installed under a domain (owner decision 2026-10-08) ──────
  // The SSL tab of /my/domains lists every certificate under the selected
  // domain, wherever it is installed. The server decides what the caller may
  // do with each row (`actions`), so nothing here infers authority.

  /**
   * `GET /cluster/certificates/registry?domain=<zone>` → `{ ok, certificates,
   * claim, missing, error }`. Never throws; a 404 means this platform does not
   * serve the domain filter yet (`missing: true`).
   */
  async listCertificatesUnderDomain(domain) {
    return loadCertificatesUnderDomain(clusterAxios, domain, { unwrap })
  },

  /**
   * `GET /cluster/certificates/registry/<id>/download?part=bundle|certificate`
   * → `{ blob, filename }`. The private key is never fetched from here: it is
   * operator-only and travels over POST with a reason (A-32).
   */
  async downloadCertificatePart(registryId, part = 'bundle') {
    const response = await clusterAxios.get(
      `/cluster/certificates/registry/${encodeURIComponent(registryId)}/download`,
      { params: { part }, responseType: 'blob' }
    )
    const disposition = (response && response.headers &&
      (response.headers['content-disposition'] || response.headers['Content-Disposition'])) || ''
    const match = /filename="?([^";]+)"?/i.exec(disposition)
    return { blob: response.data, filename: match ? match[1] : null }
  },

  /**
   * `POST /cluster/certificates/registrations/<id>/renew` — order this
   * certificate again through ACME. 202 while it is on its way.
   */
  async reissueCertificateRegistration(registrationId) {
    const response = await clusterAxios.post(
      `/cluster/certificates/registrations/${encodeURIComponent(registrationId)}/renew`, {}
    )
    return unwrap(response)
  },

  /**
   * `DELETE /cluster/certificates/registrations/<id>?revoke_certificate=true
   * &remove_dns=false` — WITHDRAW: the registration and the Kubernetes secret
   * go, so the platform stops serving and renewing the certificate. It stays
   * valid at the certificate authority until it expires and can be re-issued.
   * `remove_dns` is deliberately false — the customer's DNS records are theirs.
   */
  async withdrawCertificateRegistration(registrationId) {
    const response = await clusterAxios.delete(
      `/cluster/certificates/registrations/${encodeURIComponent(registrationId)}`,
      { params: { revoke_certificate: true, remove_dns: false } }
    )
    return unwrap(response)
  },

  /**
   * Follow a registration's progress live — SSE over `fetch` with the bearer
   * (EventSource cannot carry it), falling back to polling GET …/progress every
   * 2 s. `onEvent` gets `{type: 'progress'|'end'|'error'|'transport'}`.
   * Returns a promise that resolves with the transport used.
   */
  followCertificateProgress(registrationId, { onEvent, signal } = {}) {
    const token = store.state.auth?.accessToken || readToken(ACCESS_TOKEN_KEY)
    const headers = token ? { Authorization: `Bearer ${token}` } : {}
    return followProgress({
      url: certIssueApi.progressUrl(registrationId, clusterAxios.defaults.baseURL),
      headers,
      fetchImpl: typeof fetch === 'function' ? fetch.bind(globalThis) : null,
      poll: () => certIssueApi.progress(registrationId),
      onEvent,
      signal
    })
  },

  // ── Team Collaboration — North-South Jira connect (Phase 1, Increment 2) ────
  // The org-admin, per-org "connect your Jira account" surface. Both routes are
  // org-scoped on the backend (org id derived from the caller's identity, never
  // the request body) and gated by @require_permission('collab_connection', …).
  // The API token is write-only: it's sent on connect and NEVER returned — the
  // status projection is masked (masked email, no token). Reaches the local
  // cluster-api on :5006 through the same `/api/cluster` proxy split as getPlans().

  /**
   * Read whether the caller's org has a connected Jira. Maps to
   * `GET /api/cluster/collab/jira/status`. Returns the masked status view
   * `{ connected, site_url, email(masked), default_project_key, campaign_map,
   *    last_verified, verified_display_name, status }` (or `{ connected:false }`).
   */
  async getJiraCollabStatus() {
    const response = await clusterAxios.get('/cluster/collab/jira/status')
    return unwrap(response) || { connected: false }
  },

  /**
   * Verify + store the org's Jira credential. Maps to
   * `POST /api/cluster/collab/jira/connect`. Body:
   * `{ site_url, email, token, default_project_key? }`. The backend verifies the
   * token against Jira `GET /myself` BEFORE persisting, encrypts it at rest, and
   * returns the SAME masked status shape as getJiraCollabStatus (never the token).
   *
   * On failure the backend rejects with a unified-error envelope + HTTP status
   * (401 invalid credentials, 502 unreachable, 400 bad request, 403 pre-grant);
   * callers pass the thrown error through `classifyConnectError` in
   * utils/jira-collab.js for the right remedy copy.
   */
  async connectJiraCollab(payload) {
    const response = await clusterAxios.post('/cluster/collab/jira/connect', payload)
    return unwrap(response)
  },

  // ── Team Collaboration — North-South Jira draft→create (Phase 1, Increment 5) ─
  // The "bridge" step: turn free-text intent (+ optional cluster/namespace/
  // workload context) into a proposed Jira epic + tasks, then create it in Jira
  // only after the user confirms on the panel. Two seams:
  //
  //   requestJiraDraft — DRAFT-FETCH SEAM (see report). Runs ai-api's agentic
  //     tool loop and returns the `draft_jira_epic` proposal. As of Increment 5
  //     the ai-api transport is NOT yet wired: the website talks only to
  //     cluster-api (consistent bearer client, no browser→Jetson CORS), so this
  //     posts to a cluster-api proxy route `POST /api/cluster/collab/jira/draft`
  //     that is expected to forward to ai-api `/remediation/chat` (collab
  //     toolset) scoped to the caller's JWT. Isolated here as a single method so
  //     the confirm→create path is fully testable independent of that transport;
  //     a 404/501/503 is surfaced by classifyDraftError as an honest
  //     "assistant unavailable" state, never a crash.
  //
  //   createJiraIssues — FROZEN WRITE CONTRACT. This is the real, testable write.
  //
  // Both are org-scoped server-side and gated by
  // @require_permission('collab_connection', …); pre-grant 403 and not-connected
  // are expected states handled by utils/jira-draft.js.

  /**
   * Ask the assistant to DRAFT a Jira epic from the user's intent + scope
   * context. Maps to `POST /api/cluster/collab/jira/draft`. Body:
   * `{ intent, context: { cluster?, namespace?, workload? } }` (built by
   * utils/jira-draft.buildDraftRequest). Returns the ai-api `draft_jira_epic`
   * proposal `{ summary, description, labels[], parent_epic|null,
   * tasks:[{summary, description?}], assignee:null }` — a PROPOSAL only, nothing
   * is written. On failure, pass the thrown error through `classifyDraftError`.
   */
  async requestJiraDraft(intent, context = {}) {
    const response = await clusterAxios.post('/cluster/collab/jira/draft', { intent, context })
    return unwrap(response)
  },

  /**
   * Create the confirmed epic + tasks in Jira. Maps to the FROZEN write contract
   * `POST /api/cluster/collab/jira/issues`. Body = the (edited) draft payload:
   * `{ summary, description, labels[], parent_epic|null,
   *    tasks:[{summary, description?}], assignee:null, project? }`
   * (built by utils/jira-draft.buildIssuesPayload). Returns
   * `{ project, parent_epic, created:{ epic:{key,url}|null, tasks:[{key,url}] },
   *    partial?, errors? }` — a PARTIAL success comes back 2xx with `partial:true`
   * and is rendered honestly via `normalizeCreateResult`; a hard failure throws
   * and is mapped by `classifyCreateError`.
   */
  async createJiraIssues(payload) {
    const response = await clusterAxios.post('/cluster/collab/jira/issues', payload)
    return unwrap(response)
  }
}

export default clusterAxios

// 423 organization_locked → the locked-organisation page, once per burst.
attachOrgLockedHandler(clusterAxios)
